/**
 * Desk — Settings: Mail collection (per-user mailbox self-service + health)
 * and Clients (clients / domains / projects — the triage targets).
 * Local state only; native form controls (catalyst form controls are
 * Tailwind-4-styled and visually broken under the project's Tailwind 3).
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Heading, Subheading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import {
  fetchDeskMailboxHealth,
  fetchMyDeskMailbox,
  saveMyDeskMailbox,
  deleteMyDeskMailbox,
  testMyDeskMailbox,
  fetchDeskClients,
  createDeskClient,
  updateDeskClient,
  createDeskProject,
  fetchDeskCustomerOptions,
} from "services/desk.service";
import DeskNav from "components/desk-nav/desk-nav";
import ApiTokensTab, { FreshTokenPanel } from "./api-tokens-tab";
import { DeskMailboxHealth, DeskMailboxConfig, DeskClientEntry, DeskCustomerOption } from "types/Desk";
import { formatPrettyDate } from "utils/helperFunctions";

const STALE_AFTER_MS = 10 * 60 * 1000;

// On a phone fields are 16px (iOS zooms into anything smaller) and buttons 44px tall.
const inputCls =
  "rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400 max-md:min-h-11 max-md:text-base";
const btnCls = "rounded-lg border border-zinc-200/80 bg-transparent px-3 py-1.5 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 transition-colors duration-150 disabled:opacity-40 max-md:min-h-11";
const primaryBtnCls =
  "rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors duration-150 disabled:opacity-40 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:active:bg-indigo-600 max-md:min-h-11";
const dangerBtnCls =
  "rounded-lg bg-red-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-red-500 active:bg-red-700 shadow-sm transition-colors duration-150 disabled:opacity-40 dark:bg-red-500 dark:hover:bg-red-400 max-md:min-h-11";

export default function DeskSettings() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const [tab, setTab] = useState<"mail" | "clients" | "ai">("mail");
  // A new token is shown once, so it lives above the tabs and outside the tab
  // body: switching tab would otherwise unmount it and lose it for good.
  const [freshToken, setFreshToken] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-full p-4 md:p-6">
      <DeskNav />
      <Heading>{t("desk_settings")}</Heading>
      {freshToken && <FreshTokenPanel token={freshToken} onDismiss={() => setFreshToken(null)} />}
      <div className="no-scrollbar mt-4 flex overflow-x-auto whitespace-nowrap border-b border-zinc-200 text-sm dark:border-zinc-700">
        <button
          type="button"
          onClick={() => setTab("mail")}
          className={
            "px-4 py-2 max-md:py-3 " + (tab === "mail" ? "border-b-2 border-zinc-700 font-medium" : "text-zinc-500")
          }
        >
          {t("desk_mail_collection")}
        </button>
        <button
          type="button"
          onClick={() => setTab("clients")}
          className={
            "px-4 py-2 max-md:py-3 " +
            (tab === "clients" ? "border-b-2 border-zinc-700 font-medium" : "text-zinc-500")
          }
        >
          {t("desk_clients")}
        </button>
        <button
          type="button"
          onClick={() => setTab("ai")}
          className={
            "px-4 py-2 max-md:py-3 " + (tab === "ai" ? "border-b-2 border-zinc-700 font-medium" : "text-zinc-500")
          }
        >
          {t("api_token_tab")}
        </button>
      </div>
      {tab === "mail" ? <MailCollectionTab companyId={companyId as string} /> : null}
      {tab === "clients" ? <ClientsTab companyId={companyId as string} /> : null}
      {tab === "ai" ? <ApiTokensTab companyId={companyId as string} onCreated={setFreshToken} /> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function MailboxGuide() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("yandex");

  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-zinc-50/50 p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/40 backdrop-blur-sm">
      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2 text-sm uppercase tracking-wide">
        <span className="text-lg">💡</span> {t("desk_guide_title")}
      </h3>
      <div className="mt-4 flex gap-1.5 border-b border-zinc-200/60 pb-1.5 text-xs dark:border-zinc-800">
        {(["yandex", "gmail", "password"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-lg transition-all duration-150 ${
              activeTab === tab
                ? "bg-white font-semibold text-indigo-600 shadow-sm border border-zinc-200/50 dark:bg-zinc-800 dark:text-indigo-400 dark:border-zinc-700"
                : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300"
            }`}
          >
            {tab === "yandex" ? "Yandex" : tab === "gmail" ? "Gmail" : t("desk_guide_tab_password")}
          </button>
        ))}
      </div>

      <div className="mt-5 text-xs space-y-4 text-zinc-600 dark:text-zinc-400 leading-relaxed">
        {activeTab === "yandex" && (
          <div className="space-y-2 animate-fadeIn">
            <p className="font-semibold text-zinc-800 dark:text-zinc-200">
              {t("desk_guide_config_title", { provider: "Yandex" })}
            </p>
            <ul className="list-disc pl-4 space-y-1.5">
              <li><b>{t("desk_guide_imap_host")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">imap.yandex.com</code></li>
              <li><b>{t("desk_guide_imap_port")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">993</code> (SSL)</li>
              <li><b>{t("desk_guide_smtp_host")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">smtp.yandex.com</code></li>
              <li><b>{t("desk_guide_smtp_port")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">465</code> (SSL)</li>
              <li className="text-amber-600 dark:text-amber-400 font-medium">⚠️ {t("desk_guide_yandex_warning")}</li>
            </ul>
          </div>
        )}

        {activeTab === "gmail" && (
          <div className="space-y-2 animate-fadeIn">
            <p className="font-semibold text-zinc-800 dark:text-zinc-200">
              {t("desk_guide_config_title", { provider: "Gmail" })}
            </p>
            <ul className="list-disc pl-4 space-y-1.5">
              <li><b>{t("desk_guide_imap_host")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">imap.gmail.com</code></li>
              <li><b>{t("desk_guide_imap_port")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">993</code> (SSL)</li>
              <li><b>{t("desk_guide_smtp_host")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">smtp.gmail.com</code></li>
              <li><b>{t("desk_guide_smtp_port")}:</b> <code className="bg-zinc-200/50 px-1.5 py-0.5 rounded font-mono text-[10px] dark:bg-zinc-800">465</code> (SSL)</li>
              <li>⚠️ {t("desk_guide_gmail_imap")}</li>
            </ul>
          </div>
        )}

        {activeTab === "password" && (
          <div className="space-y-3 animate-fadeIn">
            <p className="font-semibold text-zinc-800 dark:text-zinc-200">{t("desk_guide_password_title")}</p>
            <p>{t("desk_guide_password_intro")}</p>
            <div className="space-y-2.5">
              <div className="border-l-2 border-indigo-500 pl-3">
                <span className="font-semibold block text-zinc-700 dark:text-zinc-300 text-xs">Yandex:</span>
                {t("desk_guide_password_yandex")}
              </div>
              <div className="border-l-2 border-indigo-500 pl-3">
                <span className="font-semibold block text-zinc-700 dark:text-zinc-300 text-xs">Gmail:</span>
                {t("desk_guide_password_gmail")}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Guessing the hosts from the domain lands a vardas@yandex.lt account on
// imap.yandex.lt — the very host the guide warns about — so the two providers
// we know get fixed values and only "other" keeps the guess.
const PROVIDER_PRESETS: Record<string, { imapHost: string; imapPort: string; smtpHost: string; smtpPort: string }> = {
  yandex: { imapHost: "imap.yandex.com", imapPort: "993", smtpHost: "smtp.yandex.com", smtpPort: "465" },
  gmail: { imapHost: "imap.gmail.com", imapPort: "993", smtpHost: "smtp.gmail.com", smtpPort: "465" },
};

const EMPTY_MAILBOX_FORM = {
  address: "",
  provider: "yandex",
  ...PROVIDER_PRESETS.yandex,
  appPassword: "",
  ownAddresses: "",
  ingestMode: "all",
  labelName: "",
  isActive: true,
};

type MailboxIngestError = { id: string; at: number | null; text: string };

type MailboxView = DeskMailboxConfig & {
  lastSyncedAt?: number | null;
  lastError?: string | null;
  ingestErrorCount?: number;
  recentErrors?: MailboxIngestError[];
};

const isPortValid = (value: string) => {
  const port = Number(value);
  return Boolean(value.trim()) && Number.isInteger(port) && port >= 1 && port <= 65535;
};

/**
 * The raw IMAP failure means nothing to the person who has to fix it, so the
 * three that actually happen get a sentence; anything else is shown verbatim.
 */
function mailboxErrorKey(raw: string): string | null {
  const text = raw.toUpperCase();
  if (text.includes("AUTHENTICATIONFAILED") || text.includes("INVALID CREDENTIALS") || text.includes("LOGIN FAILED")) {
    return "desk_err_auth_failed";
  }
  if (text.includes("ENOTFOUND") || text.includes("ECONNREFUSED") || text.includes("EAI_AGAIN")) {
    return "desk_err_host_unreachable";
  }
  if (text.includes("TIMEOUT") || text.includes("ETIMEDOUT")) {
    return "desk_err_timeout";
  }
  return null;
}

function MailCollectionTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [mailboxes, setMailboxes] = useState<MailboxView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_MAILBOX_FORM });
  const [hasCredentials, setHasCredentials] = useState(false);
  const [hostsTouched, setHostsTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<"disable" | "delete" | null>(null);

  const applyFormFromMailbox = useCallback((mailbox: DeskMailboxConfig) => {
    setSelectedId(mailbox.id);
    setIsAdding(false);
    setHasCredentials(mailbox.hasCredentials);
    setHostsTouched(true);
    setFeedback(null);
    setPendingRemoval(null);
    setForm({
      address: mailbox.address,
      provider: mailbox.provider,
      imapHost: mailbox.imapHost,
      imapPort: String(mailbox.imapPort),
      smtpHost: mailbox.smtpHost,
      smtpPort: String(mailbox.smtpPort),
      appPassword: "",
      ownAddresses: mailbox.ownAddresses || "",
      ingestMode: mailbox.ingestMode,
      labelName: mailbox.labelName || "",
      isActive: mailbox.isActive,
    });
  }, []);

  const loadMailboxes = useCallback(
    async (preferId?: string | null, forceAddEmpty = false) => {
      try {
        // A failed load used to fall through to "no mailboxes connected", and
        // the user would reconnect an address that is already there.
        const [healthRes, configRes] = await Promise.all([
          fetchDeskMailboxHealth(companyId),
          fetchMyDeskMailbox(companyId),
        ]);
        setLoadError(null);
        const healthById = new Map((healthRes.mailboxes || []).map((h) => [h.id, h]));
        const merged: MailboxView[] = (configRes.mailboxes || []).map((cfg) => {
          const h = healthById.get(cfg.id) as (DeskMailboxHealth & { recentErrors?: MailboxIngestError[] }) | undefined;
          return {
            ...cfg,
            lastSyncedAt: h?.lastSyncedAt ?? null,
            lastError: h?.lastError ?? null,
            ingestErrorCount: h?.ingestErrorCount ?? 0,
            recentErrors: h?.recentErrors ?? [],
          };
        });
        setMailboxes(merged);

        if (forceAddEmpty || (!merged.length && !preferId)) {
          setSelectedId(null);
          setIsAdding(true);
          setHasCredentials(false);
          setHostsTouched(false);
          setPendingRemoval(null);
          setForm({ ...EMPTY_MAILBOX_FORM });
          return;
        }

        const targetId =
          (preferId && merged.some((m) => m.id === preferId) && preferId) ||
          merged[0]?.id ||
          null;
        if (targetId) {
          const row = merged.find((m) => m.id === targetId);
          if (row) applyFormFromMailbox(row);
        }
      } catch (e: any) {
        setLoadError(e?.response?.data?.message || e?.message || t("desk_mailboxes_load_failed"));
      } finally {
        setLoading(false);
      }
    },
    [companyId, applyFormFromMailbox, t]
  );

  useEffect(() => {
    loadMailboxes();
    const timer = setInterval(() => {
      fetchDeskMailboxHealth(companyId)
        .then((d) => {
          const healthById = new Map((d.mailboxes || []).map((h) => [h.id, h]));
          setMailboxes((prev) =>
            prev.map((cfg) => {
              const h = healthById.get(cfg.id) as
                | (DeskMailboxHealth & { recentErrors?: MailboxIngestError[] })
                | undefined;
              if (!h) return cfg;
              return {
                ...cfg,
                lastSyncedAt: h.lastSyncedAt,
                lastError: h.lastError,
                ingestErrorCount: h.ingestErrorCount,
                recentErrors: h.recentErrors ?? [],
                isActive: h.isActive,
              };
            })
          );
        })
        .catch(() => undefined);
    }, 30000);
    return () => clearInterval(timer);
  }, [companyId, loadMailboxes]);

  const startAddMailbox = () => {
    setIsAdding(true);
    setSelectedId(null);
    setHasCredentials(false);
    setHostsTouched(false);
    setFeedback(null);
    setPendingRemoval(null);
    setForm({ ...EMPTY_MAILBOX_FORM });
  };

  const selectMailbox = (mailbox: MailboxView) => {
    applyFormFromMailbox(mailbox);
  };

  const set = (key: string, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }));

  const editingId = isAdding ? null : selectedId;
  const selectedMailbox = editingId ? mailboxes.find((m) => m.id === editingId) || null : null;
  // Changing the address of a connected mailbox points the stored password at a
  // foreign account, so it has to be re-entered — the server insists too.
  const addressChanged = Boolean(
    selectedMailbox && selectedMailbox.address !== form.address.trim().toLowerCase()
  );
  // Hard delete is only offered once collection has actually stopped.
  const stillConnected = Boolean(selectedMailbox && (selectedMailbox.isActive || hasCredentials));
  const portsValid = isPortValid(form.imapPort) && isPortValid(form.smtpPort);

  const payload = () => ({
    // Without an explicit intent the server upserted by company+user, so
    // "Add mailbox" overwrote the mailbox that was already connected.
    ...(editingId ? { id: editingId, mailboxId: editingId } : { create: true }),
    address: form.address,
    provider: form.provider,
    imapHost: form.imapHost || undefined,
    imapPort: Number(form.imapPort),
    smtpHost: form.smtpHost || undefined,
    smtpPort: Number(form.smtpPort),
    appPassword: form.appPassword || undefined,
    ownAddresses: form.ownAddresses || undefined,
    ingestMode: form.ingestMode,
    labelName: form.labelName || undefined,
    isActive: form.isActive,
  });

  // Field-bound problems stay inline next to the field; everything else is the
  // result of a mutation and belongs in a toast.
  const saveErrorText = (e: any) => {
    const data = e?.response?.data || {};
    if (data.code === "ADDRESS_TAKEN") return t("desk_address_taken");
    if (data.code === "PASSWORD_REQUIRED") return t("desk_app_password_required");
    if (data.code === "PASSWORD_REQUIRED_ADDRESS_CHANGE") return t("desk_password_required_address_change");
    if (data.code === "ADDRESS_HELD") return t("desk_address_held", { count: data.cardCount || 0 });
    return null;
  };

  const test = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await testMyDeskMailbox(companyId, payload());
      if (res.ok) {
        toast.success(
          res.sentFolder
            ? t("desk_connection_ok_sent", { folder: res.sentFolder })
            : t("desk_connection_ok")
        );
      } else {
        toast.error(t("desk_connection_failed"));
        setFeedback({ ok: false, text: t("desk_connection_failed_hint") });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setFeedback(null);
    if (!portsValid) {
      setFeedback({ ok: false, text: t("desk_invalid_port") });
      return;
    }
    const normalizedAddress = form.address.trim().toLowerCase();
    const duplicate = mailboxes.find(
      (m) => m.address === normalizedAddress && m.id !== editingId
    );
    if (duplicate) {
      setFeedback({ ok: false, text: t("desk_address_taken") });
      return;
    }
    if (addressChanged && !form.appPassword) {
      setFeedback({ ok: false, text: t("desk_password_required_address_change") });
      return;
    }
    setBusy(true);
    try {
      const res = await saveMyDeskMailbox(companyId, payload());
      setForm((f) => ({ ...f, appPassword: "" }));
      setHasCredentials(true);
      setIsAdding(false);
      const newId = res?.mailboxId || editingId;
      if (newId) setSelectedId(newId);
      await loadMailboxes(newId);
      toast.success(t("desk_saved"));
    } catch (e: any) {
      const fieldError = saveErrorText(e);
      if (fieldError) {
        setFeedback({ ok: false, text: fieldError });
      } else {
        toast.error(e?.response?.data?.message || e?.message || t("error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const removeMailbox = async (mode: "disable" | "delete") => {
    if (!editingId) return;
    setBusy(true);
    try {
      await deleteMyDeskMailbox(companyId, editingId, mode);
      setPendingRemoval(null);
      toast.success(mode === "delete" ? t("desk_mailbox_deleted") : t("desk_mailbox_disconnected"));
      await loadMailboxes(mode === "delete" ? null : editingId);
    } catch (e: any) {
      const data = e?.response?.data || {};
      toast.error(
        data.code === "MAILBOX_HAS_CARDS"
          ? t("desk_mailbox_has_cards", { count: data.cardCount || 0 })
          : data.code === "MAILBOX_STILL_CONNECTED"
            ? t("desk_mailbox_disconnect_first")
            : data.message || e?.message || t("error")
      );
    } finally {
      setBusy(false);
    }
  };

  const now = Date.now();
  const showForm = !loadError && (isAdding || !!selectedId);

  const providerLabel = (p: string) =>
    p === "yandex" ? "Yandex" : p === "gmail" ? "Gmail" : t("desk_provider_other");

  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Subheading className="!font-semibold !mb-1">{t("desk_mailboxes")}</Subheading>
          <Text className="!text-sm !text-zinc-500">{t("desk_mailboxes_manage_hint")}</Text>
        </div>
        <button type="button" onClick={startAddMailbox} className={primaryBtnCls} disabled={busy}>
          {t("desk_add_mailbox")}
        </button>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 animate-pulse">
          <div className="h-28 rounded-2xl bg-zinc-100 dark:bg-zinc-900" />
          <div className="h-28 rounded-2xl bg-zinc-100 dark:bg-zinc-900" />
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-400">
          <div className="font-medium">{t("desk_mailboxes_load_failed")}</div>
          <div className="mt-1 text-xs break-words">{loadError}</div>
          <button
            type="button"
            className={btnCls + " mt-3"}
            onClick={() => {
              setLoading(true);
              loadMailboxes(selectedId);
            }}
          >
            {t("retry")}
          </button>
        </div>
      ) : mailboxes.length === 0 && !isAdding ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 p-10 text-center dark:border-zinc-700 dark:bg-zinc-900/30">
          <Text className="text-zinc-500 font-medium">{t("desk_no_mailboxes")}</Text>
          <button type="button" onClick={startAddMailbox} className={primaryBtnCls + " mt-4"}>
            {t("desk_add_mailbox")}
          </button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {mailboxes.map((m) => {
            const isSelected = !isAdding && selectedId === m.id;
            const isStale = !m.lastSyncedAt || now - (m.lastSyncedAt || 0) > STALE_AFTER_MS;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => selectMailbox(m)}
                className={
                  "rounded-2xl border p-4 text-left transition-all " +
                  (isSelected
                    ? "border-indigo-500 bg-indigo-50/60 shadow-sm ring-1 ring-indigo-500/30 dark:border-indigo-400 dark:bg-indigo-950/30"
                    : "border-zinc-200/80 bg-white hover:border-zinc-300 hover:bg-zinc-50/80 dark:border-zinc-800 dark:bg-zinc-950 dark:hover:border-zinc-700")
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-zinc-900 dark:text-zinc-100">{m.address}</div>
                    <div className="mt-1 text-xs text-zinc-500">
                      {providerLabel(m.provider)}
                      {" · "}
                      {m.ingestMode === "label"
                        ? `${t("desk_ingest_mode_label")}${m.labelName ? `: ${m.labelName}` : ""}`
                        : t("desk_ingest_mode_all")}
                    </div>
                  </div>
                  {/* A freshly connected mailbox is not broken, it just has not
                      synced yet — and a green OK next to a pile of ingest
                      errors is a lie. */}
                  {!m.isActive ? (
                    <Badge color="zinc">{t("desk_mailbox_disabled")}</Badge>
                  ) : !m.lastSyncedAt ? (
                    <Badge color="amber">{t("desk_awaiting_first_sync")}</Badge>
                  ) : isStale ? (
                    <Badge color="red">{t("desk_sync_stale")}</Badge>
                  ) : m.ingestErrorCount ? (
                    <Badge color="amber">
                      {t("desk_ingest_errors_7d")}: {m.ingestErrorCount}
                    </Badge>
                  ) : (
                    <Badge color="green">{t("desk_sync_ok")}</Badge>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-1 text-[11px] text-zinc-500 font-mono">
                  <div className="break-all">
                    IMAP {m.imapHost}:{m.imapPort}
                  </div>
                  <div className="break-all">
                    SMTP {m.smtpHost}:{m.smtpPort}
                  </div>
                  <div className="break-words">
                    {t("desk_last_sync")}:{" "}
                    {m.lastSyncedAt ? formatPrettyDate(m.lastSyncedAt, "lt-LT", true) : t("desk_never_synced")}
                    {m.ingestErrorCount ? ` · ${t("desk_ingest_errors_7d")}: ${m.ingestErrorCount}` : ""}
                  </div>
                </div>
                {m.lastError ? (
                  <div className="mt-2 rounded-lg bg-red-500/10 px-2 py-1.5 text-[11px] text-red-700 break-words dark:text-red-400">
                    {mailboxErrorKey(m.lastError) ? t(mailboxErrorKey(m.lastError) as string) : m.lastError}
                  </div>
                ) : null}
              </button>
            );
          })}
          {isAdding && (
            <div className="rounded-2xl border border-dashed border-indigo-400 bg-indigo-50/40 p-4 dark:border-indigo-500 dark:bg-indigo-950/20">
              <div className="font-semibold text-indigo-700 dark:text-indigo-300">{t("desk_add_mailbox_title")}</div>
              <Text className="!mt-1 !text-xs !text-zinc-500">{t("desk_mailboxes_new_hint")}</Text>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl border border-zinc-200/80 bg-white p-4 sm:p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 space-y-5">
            <Subheading className="!font-semibold">
              {editingId ? t("desk_edit_mailbox_title") : t("desk_add_mailbox_title")}
            </Subheading>
            <div className="space-y-4 text-sm">
              <label className="block">
                <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_address")}</span>
                <input
                  value={form.address}
                  onChange={(e) => {
                    const address = e.target.value.trim().toLowerCase();
                    setForm((f) => {
                      const domain = address.split("@")[1];
                      const next = { ...f, address };
                      // Only "other" providers get their hosts guessed; the
                      // known ones already carry their preset.
                      if (f.provider === "other" && !hostsTouched && domain && domain.includes(".")) {
                        next.imapHost = "imap." + domain;
                        next.smtpHost = "smtp." + domain;
                      }
                      return next;
                    });
                  }}
                  placeholder="name@example.com"
                  className={inputCls + " w-full"}
                />
              </label>
              {addressChanged && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
                  {t("desk_address_change_warning")}
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_provider")}</span>
                  <select
                    value={form.provider}
                    onChange={(e) => {
                      const provider = e.target.value;
                      setForm((f) => ({
                        ...f,
                        provider,
                        // Label mode rides on Gmail's labels; the server refuses
                        // it anywhere else, so it cannot stay selected.
                        ingestMode: provider === "gmail" ? f.ingestMode : "all",
                        ...(PROVIDER_PRESETS[provider] && !hostsTouched ? PROVIDER_PRESETS[provider] : {}),
                      }));
                    }}
                    className={inputCls + " w-full"}
                  >
                    <option value="yandex">Yandex</option>
                    <option value="gmail">Gmail</option>
                    <option value="other">{t("desk_provider_other")}</option>
                  </select>
                </label>
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_app_password")}</span>
                  <input
                    type="password"
                    value={form.appPassword}
                    onChange={(e) => set("appPassword", e.target.value)}
                    placeholder={hasCredentials && !addressChanged ? t("desk_app_password_saved") : ""}
                    className={inputCls + " w-full"}
                  />
                </label>
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_imap_host_port")}</span>
                  <div className="flex gap-2">
                    <input
                      value={form.imapHost}
                      onChange={(e) => {
                        setHostsTouched(true);
                        set("imapHost", e.target.value);
                      }}
                      className={inputCls + " min-w-0 flex-1"}
                    />
                    <input
                      type="number"
                      min={1}
                      max={65535}
                      value={form.imapPort}
                      onChange={(e) => set("imapPort", e.target.value)}
                      className={inputCls + " w-20 shrink-0"}
                    />
                  </div>
                  {!isPortValid(form.imapPort) && (
                    <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{t("desk_invalid_port")}</span>
                  )}
                </label>
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_smtp_host_port")}</span>
                  <div className="flex gap-2">
                    <input
                      value={form.smtpHost}
                      onChange={(e) => {
                        setHostsTouched(true);
                        set("smtpHost", e.target.value);
                      }}
                      className={inputCls + " min-w-0 flex-1"}
                    />
                    <input
                      type="number"
                      min={1}
                      max={65535}
                      value={form.smtpPort}
                      onChange={(e) => set("smtpPort", e.target.value)}
                      className={inputCls + " w-20 shrink-0"}
                    />
                  </div>
                  {!isPortValid(form.smtpPort) && (
                    <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{t("desk_invalid_port")}</span>
                  )}
                </label>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_own_addresses")}</span>
                <input
                  value={form.ownAddresses}
                  onChange={(e) => set("ownAddresses", e.target.value)}
                  placeholder="me@a.com, me@b.com"
                  className={inputCls + " w-full"}
                />
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_ingest_mode")}</span>
                  <select
                    value={form.ingestMode}
                    onChange={(e) => set("ingestMode", e.target.value)}
                    className={inputCls + " w-full"}
                  >
                    <option value="all">{t("desk_ingest_mode_all")}</option>
                    <option value="label" disabled={form.provider !== "gmail"}>
                      {t("desk_ingest_mode_label")}
                    </option>
                  </select>
                  {form.provider !== "gmail" && (
                    <span className="mt-1 block text-xs text-zinc-500">{t("desk_ingest_mode_label_gmail_only")}</span>
                  )}
                </label>
                {form.ingestMode === "label" && (
                  <label className="block min-w-0">
                    <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_label_name")}</span>
                    <input
                      value={form.labelName}
                      onChange={(e) => set("labelName", e.target.value)}
                      className={inputCls + " w-full"}
                    />
                  </label>
                )}
              </div>
              <label className="flex cursor-pointer items-center gap-2.5 select-none py-1 text-zinc-700 dark:text-zinc-300 font-medium max-md:min-h-11">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                  className="size-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                />
                {t("desk_mailbox_active")}
              </label>
              {feedback && (
                <div
                  className={
                    "rounded-xl p-3 text-sm font-medium transition-all duration-200 border shadow-sm " +
                    (feedback.ok
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
                      : "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20")
                  }
                >
                  {feedback.text}
                </div>
              )}
              <div className="flex flex-wrap gap-3 pt-2 border-t border-zinc-100 dark:border-zinc-900">
                <button
                  type="button"
                  onClick={test}
                  disabled={busy || !form.address || !portsValid}
                  className={btnCls}
                >
                  {t("desk_test_connection")}
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy || !form.address || !portsValid}
                  className={primaryBtnCls}
                >
                  {editingId ? t("desk_save") : t("desk_connect_mailbox")}
                </button>
                {isAdding && mailboxes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => selectMailbox(mailboxes[0])}
                    disabled={busy}
                    className={btnCls}
                  >
                    {t("cancel")}
                  </button>
                )}
              </div>
              {selectedMailbox && selectedMailbox.recentErrors && selectedMailbox.recentErrors.length > 0 && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                  <div className="font-medium text-amber-700 dark:text-amber-400">
                    {t("desk_recent_ingest_errors")}
                  </div>
                  <ul className="mt-2 space-y-1.5 text-zinc-600 dark:text-zinc-400">
                    {selectedMailbox.recentErrors.map((row) => (
                      <li key={row.id} className="break-words">
                        <span className="font-mono text-[10px] text-zinc-500">
                          {row.at ? formatPrettyDate(row.at, "lt-LT", true) : ""}
                        </span>{" "}
                        {row.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {editingId && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-3">
                  <div>
                    <div className="text-sm font-medium text-red-700 dark:text-red-400">
                      {t("desk_mailbox_danger_zone")}
                    </div>
                    <Text className="!mt-1 !text-xs !text-zinc-500">{t("desk_mailbox_danger_hint")}</Text>
                  </div>
                  {pendingRemoval ? (
                    <div className="space-y-3">
                      <div className="text-sm text-zinc-700 dark:text-zinc-300">
                        {t(
                          pendingRemoval === "delete"
                            ? "desk_mailbox_delete_confirm"
                            : "desk_mailbox_disconnect_confirm",
                          { address: selectedMailbox?.address }
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => removeMailbox(pendingRemoval)}
                          disabled={busy}
                          className={dangerBtnCls}
                        >
                          {pendingRemoval === "delete" ? t("desk_mailbox_delete") : t("desk_mailbox_disconnect")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingRemoval(null)}
                          disabled={busy}
                          className={btnCls}
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => setPendingRemoval("disable")}
                        disabled={busy}
                        className={btnCls}
                      >
                        {t("desk_mailbox_disconnect")}
                      </button>
                      {/* The worker keeps an IMAP runner for a connected
                          mailbox for up to a minute after the row is gone, so
                          the server insists on disconnect-then-delete. */}
                      <button
                        type="button"
                        onClick={() => setPendingRemoval("delete")}
                        disabled={busy || stillConnected}
                        title={stillConnected ? t("desk_mailbox_disconnect_first") : undefined}
                        className={btnCls + " !text-red-600 dark:!text-red-400 disabled:opacity-40"}
                      >
                        {t("desk_mailbox_delete")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <MailboxGuide />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function ClientsTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [clients, setClients] = useState<DeskClientEntry[]>([]);
  const [customers, setCustomers] = useState<DeskCustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [newName, setNewName] = useState("");
  const [newDomains, setNewDomains] = useState("");
  const [domainEdits, setDomainEdits] = useState<Record<string, string>>({});
  const [projectEdits, setProjectEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [customersTruncated, setCustomersTruncated] = useState<{ shown: number; total: number } | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerListOpen, setCustomerListOpen] = useState(false);

  const load = useCallback(
    (savedClientId?: string) =>
      fetchDeskClients(companyId)
        .then((d) => {
          const entries: DeskClientEntry[] = d.entries || [];
          setClients(entries);
          // Merge instead of replace: reloading after one Save used to throw
          // away the domains still being typed in every other card.
          setDomainEdits((prev) => {
            const next = { ...prev };
            entries.forEach((c) => {
              if (next[c.id] === undefined || c.id === savedClientId) {
                next[c.id] = c.domains.join(", ");
              }
            });
            return next;
          });
          setLoadError(null);
        })
        .catch((e) => setLoadError(e?.response?.data?.message || e?.message || t("desk_clients_load_failed")))
        .finally(() => setLoading(false)),
    [companyId, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const loadCustomers = useCallback(() => {
    fetchDeskCustomerOptions(companyId)
      .then((d) => {
        setCustomers(d.entries || []);
        setCustomersTruncated(
          d.truncated ? { shown: (d.entries || []).length, total: d.total || 0 } : null
        );
      })
      .catch(() => setCustomers([]));
  }, [companyId]);

  useEffect(loadCustomers, [loadCustomers]);

  const addClient = async () => {
    if (!selectedCustomer && !newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await createDeskClient(companyId, {
        customer: selectedCustomer || undefined,
        name: newName.trim() || undefined,
        domains: newDomains.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setSelectedCustomer("");
      setCustomerQuery("");
      setNewName("");
      setNewDomains("");
      await load();
      loadCustomers();
      // A domain owned by another client is dropped by the server; saying so is
      // the difference between "routing is set up" and "nothing happened".
      if (result?.skippedDomains?.length) {
        setError(t("desk_domains_taken") + ": " + result.skippedDomains.join(", "));
      }
      toast.success(t("desk_client_added"));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const availableCustomers = customers.filter((c) => !c.alreadyLinked);
  const selectedCustomerName = availableCustomers.find((c) => c.id === selectedCustomer)?.name || "";
  const customerMatches = customerQuery.trim()
    ? availableCustomers.filter((c) => c.name.toLowerCase().includes(customerQuery.trim().toLowerCase()))
    : availableCustomers;

  return (
    <div className="mt-6 space-y-6">
      <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 flex flex-wrap items-end gap-3.5">
        {/* A native select over hundreds of synced customers cannot be searched,
            so the field filters as you type. */}
        <div className="relative block text-sm flex-1 min-w-[200px]">
          <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_pick_existing_client")}</span>
          <input
            value={selectedCustomer ? selectedCustomerName : customerQuery}
            onChange={(e) => {
              setSelectedCustomer("");
              setCustomerQuery(e.target.value);
              setCustomerListOpen(true);
            }}
            onFocus={() => setCustomerListOpen(true)}
            onBlur={() => window.setTimeout(() => setCustomerListOpen(false), 120)}
            placeholder={t("desk_pick_customer_placeholder")}
            className={inputCls + " w-full"}
          />
          {customerListOpen && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
              {customerMatches.slice(0, 50).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setSelectedCustomer(c.id);
                    setCustomerQuery("");
                    setCustomerListOpen(false);
                  }}
                  className="block w-full px-3 py-1.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 max-md:py-3"
                >
                  {c.name}
                </button>
              ))}
              {!customerMatches.length && (
                <div className="px-3 py-2 text-xs text-zinc-500">{t("desk_no_customers_found")}</div>
              )}
            </div>
          )}
          {customersTruncated && (
            <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
              {t("desk_customer_list_truncated", customersTruncated)}
            </span>
          )}
        </div>
        {!selectedCustomer && (
          <label className="block text-sm flex-1 min-w-[200px]">
            <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_client_name")}</span>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} className={inputCls + " w-full"} />
          </label>
        )}
        <label className="block text-sm flex-1 min-w-[200px]">
          <span className="mb-1.5 block text-zinc-500 font-medium">{t("desk_domains")}</span>
          <input
            value={newDomains}
            onChange={(e) => setNewDomains(e.target.value)}
            placeholder="acme.com, acme.lt"
            className={inputCls + " w-full"}
          />
        </label>
        <button
          type="button"
          onClick={addClient}
          disabled={busy || (!selectedCustomer && !newName.trim())}
          className={primaryBtnCls}
        >
          {t("desk_add_client")}
        </button>
        {error && (
          <div className="w-full rounded-xl bg-red-500/10 p-3 text-sm text-red-700 border border-red-500/20 dark:text-red-400">{error}</div>
        )}
      </div>
      {loadError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-400">
          <div className="font-medium">{t("desk_clients_load_failed")}</div>
          <div className="mt-1 text-xs break-words">{loadError}</div>
          <button
            type="button"
            className={btnCls + " mt-3"}
            onClick={() => {
              setLoading(true);
              load();
            }}
          >
            {t("retry")}
          </button>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 animate-pulse">
          <div className="h-40 rounded-xl bg-zinc-100 dark:bg-zinc-900" />
          <div className="h-40 rounded-xl bg-zinc-100 dark:bg-zinc-900" />
        </div>
      )}
      {!loading && !loadError && !clients.length && (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 p-10 text-center dark:border-zinc-700 dark:bg-zinc-900/30">
          <Text className="text-zinc-500 font-medium">{t("desk_no_clients")}</Text>
          <Text className="!mt-1 !text-sm !text-zinc-500">{t("desk_no_clients_hint")}</Text>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {clients.map((client) => (
          <div key={client.id} className="rounded-xl border border-zinc-200/80 bg-white p-5 shadow-sm hover:shadow-md transition-all duration-200 dark:border-zinc-800 dark:bg-zinc-950/80">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 dark:border-zinc-900">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 font-bold dark:bg-indigo-950/50 dark:text-indigo-400">
                  {client.name.substring(0, 2).toUpperCase()}
                </div>
                <Subheading className="!font-semibold text-zinc-900 dark:text-zinc-100">{client.name}</Subheading>
              </div>
              {!client.isActive && <Badge color="zinc">{t("desk_mailbox_disabled")}</Badge>}
            </div>
            
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-zinc-500 font-medium w-16 shrink-0 max-md:w-full">{t("desk_domains")}:</span>
                <input
                  value={domainEdits[client.id] ?? ""}
                  onChange={(e) => setDomainEdits((d) => ({ ...d, [client.id]: e.target.value }))}
                  className={inputCls + " min-w-0 flex-1 py-1 px-2.5"}
                />
                <button
                  type="button"
                  className={btnCls + " py-1 px-2.5"}
                  onClick={async () => {
                    try {
                      const result = await updateDeskClient(companyId, client.id, {
                        domains: (domainEdits[client.id] || "")
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      });
                      // Domains are a field of this card, so their problem
                      // belongs in this card and not in a banner two screens up.
                      setClientErrors((errors) => ({
                        ...errors,
                        [client.id]:
                          result.skippedDomains && result.skippedDomains.length
                            ? t("desk_domains_taken") + ": " + result.skippedDomains.join(", ")
                            : "",
                      }));
                      await load(client.id);
                      toast.success(t("desk_saved"));
                    } catch (e: any) {
                      setClientErrors((errors) => ({
                        ...errors,
                        [client.id]: e?.response?.data?.message || e?.message || t("error"),
                      }));
                    }
                  }}
                >
                  {t("desk_save")}
                </button>
              </div>
              {clientErrors[client.id] && (
                <div className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-700 break-words dark:text-red-400">
                  {clientErrors[client.id]}
                </div>
              )}

              <div className="flex flex-wrap items-start gap-2 text-sm pt-3 border-t border-zinc-100 dark:border-zinc-900">
                <span className="text-zinc-500 font-medium w-16 shrink-0 pt-1 max-md:w-full">{t("desk_projects")}:</span>
                <div className="min-w-0 flex-1 space-y-3 max-md:basis-full">
                  <div className="flex flex-wrap gap-1.5">
                    {client.projects.map((p) => (
                      <Badge key={p.id} color="purple" className="px-2 py-0.5 rounded text-xs font-semibold">
                        {p.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={projectEdits[client.id] ?? ""}
                      onChange={(e) => setProjectEdits((d) => ({ ...d, [client.id]: e.target.value }))}
                      placeholder={t("desk_new_project")}
                      className={inputCls + " min-w-0 flex-1 py-1 px-2.5"}
                    />
                    <button
                      type="button"
                      className={btnCls + " py-1 px-2.5"}
                      disabled={!(projectEdits[client.id] || "").trim()}
                      onClick={async () => {
                        try {
                          await createDeskProject(companyId, {
                            client: client.id,
                            name: (projectEdits[client.id] || "").trim(),
                          });
                          setProjectEdits((d) => ({ ...d, [client.id]: "" }));
                          setClientErrors((errors) => ({ ...errors, [client.id]: "" }));
                          await load();
                          toast.success(t("desk_project_added"));
                        } catch (e: any) {
                          setClientErrors((errors) => ({
                            ...errors,
                            [client.id]: e?.response?.data?.message || e?.message || t("error"),
                          }));
                        }
                      }}
                    >
                      {t("desk_add_project")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
